import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Trophy, Calendar, TrendingUp, Star, X, BookOpen, Headphones, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const toLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateKeyToEpoch = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

const extractLocalDateFromRow = (row) => {
  if (row?.date && /^\d{4}-\d{2}-\d{2}$/.test(row.date)) {
    return row.date;
  }
  if (row?.created_at) {
    return toLocalDateKey(new Date(row.created_at));
  }
  return null;
};

const getListeningMinutesFromLog = (log) => {
  const value = Number(log?.listening_minutes ?? log?.time_spent ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const getReadSessionsFromLog = (log) => {
  const explicit = Number(log?.read_sessions);
  if (Number.isFinite(explicit)) return explicit;
  return (Number(log?.pages_read || 0) > 0) ? 1 : 0;
};

const getListeningSessionsFromLog = (log) => {
  const explicit = Number(log?.listening_sessions);
  if (Number.isFinite(explicit)) return explicit;
  return getListeningMinutesFromLog(log) > 0 ? 1 : 0;
};

const isQuranReadActivityName = (name = '') => {
  const lower = String(name).toLowerCase();
  return (lower.includes('quran') || lower.includes('kur')) && (lower.includes('read') || lower.includes('čitan'));
};

const isQuranListenActivityName = (name = '') => {
  const lower = String(name).toLowerCase();
  return (lower.includes('quran') || lower.includes('kur')) && (lower.includes('listen') || lower.includes('sluš'));
};

// Get user's daily streak data
const getStreakData = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const today = new Date();
  const todayKey = toLocalDateKey(today);
  
  // Get bereket + Quran activity for the last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const minDayKey = toLocalDateKey(thirtyDaysAgo);
  
  const [logsRes, quranRes] = await Promise.all([
    supabase
      .from('bereket_logs')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', minDayKey)
      .order('created_at', { ascending: false }),
    supabase
      .from('quran_reading_progress')
      .select('*')
      .eq('user_id', user.id)
      .gte('created_at', thirtyDaysAgo.toISOString())
  ]);

  const logs = logsRes.data || [];
  const quranLogs = quranRes.data || [];

  // Calculate streak from both sources
  const uniqueDays = new Set();
  [...logs, ...quranLogs].forEach((row) => {
    const day = extractLocalDateFromRow(row);
    if (day && day >= minDayKey) {
      uniqueDays.add(day);
    }
  });

  // Calculate current streak
  let currentStreak = 0;
  let checkDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  while (true) {
    const dateStr = toLocalDateKey(checkDate);
    if (uniqueDays.has(dateStr)) {
      currentStreak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else if (dateStr === todayKey) {
      // Today not yet logged, check yesterday
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }

  // Calculate longest streak
  const sortedDays = Array.from(uniqueDays).sort();
  let longestStreak = 0;
  let tempStreak = 0;
  let prevEpoch = null;

  sortedDays.forEach(day => {
    const epoch = dateKeyToEpoch(day);
    if (prevEpoch !== null) {
      const diff = (epoch - prevEpoch) / (1000 * 60 * 60 * 24);
      if (diff === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    } else {
      tempStreak = 1;
    }
    prevEpoch = epoch;
  });
  longestStreak = Math.max(longestStreak, tempStreak);

  return {
    currentStreak,
    longestStreak,
    totalActiveDays: uniqueDays.size,
    hasLoggedToday: uniqueDays.has(todayKey)
  };
};

// Get Quran reading stats
const getQuranStats = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const minDateKey = toLocalDateKey(thirtyDaysAgo);

  // Get reading progress (marked ayahs)
  const { data: readProgress } = await supabase
    .from('quran_reading_progress')
    .select('*')
    .eq('user_id', user.id);

  // Get daily logs
  const { data: dailyLogs } = await supabase
    .from('quran_daily_logs')
    .select('*')
    .eq('user_id', user.id)
    .gte('date', minDateKey);

  // Get bereket logs (fallback source for Quran read/listen sessions)
  const { data: bereketLogsWithJoin } = await supabase
    .from('bereket_logs')
    .select('date,activity_id,bereket_activities(name_en)')
    .eq('user_id', user.id)
    .gte('date', minDateKey);

  let bereketLogs = bereketLogsWithJoin || [];
  let activityNameById = new Map();

  bereketLogs.forEach((row) => {
    const name = row?.bereket_activities?.name_en;
    if (row?.activity_id && name) {
      activityNameById.set(row.activity_id, String(name));
    }
  });

  if (activityNameById.size === 0 && bereketLogs.length > 0) {
    const activityIds = [...new Set(bereketLogs.map(log => log.activity_id).filter(Boolean))];
    if (activityIds.length > 0) {
      const { data: activityMeta } = await supabase
        .from('bereket_activities')
        .select('id,name_en')
        .in('id', activityIds);

      (activityMeta || []).forEach((row) => {
        if (row?.id && row?.name_en) {
          activityNameById.set(row.id, String(row.name_en));
        }
      });
    }

    bereketLogs = bereketLogs.map((row) => ({
      ...row,
      bereket_activities: {
        name_en: activityNameById.get(row.activity_id) || ''
      }
    }));
  }

  // Get juz progress
  const { data: juzProgress } = await supabase
    .from('quran_juz_progress')
    .select('*')
    .eq('user_id', user.id);

  // Calculate ayah stats (supports both ayah_number and ayah_start/ayah_end schemas)
  const uniqueAyahs = new Set();
  let rangedAyahCount = 0;
  (readProgress || []).forEach((row) => {
    if (Number.isFinite(row?.surah_number) && Number.isFinite(row?.ayah_number)) {
      uniqueAyahs.add(`${row.surah_number}:${row.ayah_number}`);
    } else if (
      Number.isFinite(row?.surah_number)
      && Number.isFinite(row?.ayah_start)
      && Number.isFinite(row?.ayah_end)
      && row.ayah_end >= row.ayah_start
    ) {
      rangedAyahCount += (row.ayah_end - row.ayah_start + 1);
    }
  });

  const totalAyahsRead = uniqueAyahs.size + rangedAyahCount;
  const totalPagesRead = dailyLogs?.reduce((sum, log) => sum + (log.pages_read || 0), 0) || 0;
  const totalListeningMinutesFromLogs = dailyLogs?.reduce((sum, log) => sum + getListeningMinutesFromLog(log), 0) || 0;
  const totalReadSessionsFromLogs = dailyLogs?.reduce((sum, log) => sum + getReadSessionsFromLog(log), 0) || 0;
  const totalListeningSessionsFromLogs = dailyLogs?.reduce((sum, log) => sum + getListeningSessionsFromLog(log), 0) || 0;

  const quranReadSessionsFromBereket = bereketLogs.filter(log =>
    isQuranReadActivityName(log?.bereket_activities?.name_en)
  ).length;
  const quranListenSessionsFromBereket = bereketLogs.filter(log =>
    isQuranListenActivityName(log?.bereket_activities?.name_en)
  ).length;

  const totalReadSessions = Math.max(totalReadSessionsFromLogs, quranReadSessionsFromBereket);
  const totalListeningSessions = Math.max(totalListeningSessionsFromLogs, quranListenSessionsFromBereket);
  const totalListeningMinutes = totalListeningMinutesFromLogs > 0
    ? totalListeningMinutesFromLogs
    : totalListeningSessions;
  const completedJuz = juzProgress?.filter(j => j.completed)?.length || 0;

  // Calculate weekly progress
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekAgoKey = toLocalDateKey(weekAgo);
  const weeklyLogs = (dailyLogs || []).filter((log) => {
    const key = extractLocalDateFromRow(log);
    return key && key >= weekAgoKey;
  });
  const weeklyPages = weeklyLogs.reduce((sum, log) => sum + (log.pages_read || 0), 0);
  const weeklyMinutesFromLogs = weeklyLogs.reduce((sum, log) => sum + getListeningMinutesFromLog(log), 0);
  const weeklyReadSessionsFromLogs = weeklyLogs.reduce((sum, log) => sum + getReadSessionsFromLog(log), 0);
  const weeklyListeningSessionsFromLogs = weeklyLogs.reduce((sum, log) => sum + getListeningSessionsFromLog(log), 0);

  const weeklyBereketLogs = bereketLogs.filter((log) => {
    const key = log?.date;
    return key && key >= weekAgoKey;
  });

  const weeklyReadSessionsFromBereket = weeklyBereketLogs.filter(log =>
    isQuranReadActivityName(log?.bereket_activities?.name_en)
  ).length;
  const weeklyListeningSessionsFromBereket = weeklyBereketLogs.filter(log =>
    isQuranListenActivityName(log?.bereket_activities?.name_en)
  ).length;

  const weeklyReadSessions = Math.max(weeklyReadSessionsFromLogs, weeklyReadSessionsFromBereket);
  const weeklyListeningSessions = Math.max(weeklyListeningSessionsFromLogs, weeklyListeningSessionsFromBereket);
  const weeklyMinutes = weeklyMinutesFromLogs > 0 ? weeklyMinutesFromLogs : weeklyListeningSessions;

  return {
    totalAyahsRead,
    totalPagesRead,
    totalListeningMinutes,
    totalReadSessions,
    totalListeningSessions,
    completedJuz,
    weeklyPages,
    weeklyMinutes,
    weeklyReadSessions,
    weeklyListeningSessions,
    totalQuranPages: 604,
    progressPercent: Math.round((totalPagesRead / 604) * 100)
  };
};

export default function DailyStreakStats({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const [streakData, setStreakData] = useState(null);
  const [quranStats, setQuranStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('streak');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [streak, quran] = await Promise.all([
        getStreakData(),
        getQuranStats()
      ]);
      setStreakData(streak);
      setQuranStats(quran);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStreakEmoji = (streak) => {
    if (streak >= 30) return '🏆';
    if (streak >= 14) return '⭐';
    if (streak >= 7) return '🔥';
    if (streak >= 3) return '✨';
    return '💫';
  };

  const getStreakMessage = (streak, hasLoggedToday) => {
    if (!hasLoggedToday) {
      return lang === 'hr' 
        ? 'Još nisi zabilježio ništa danas! Nastavi svoj niz.'
        : "You haven't logged anything today! Keep your streak going.";
    }
    if (streak >= 30) {
      return lang === 'hr' ? 'Nevjerojatno! Pravi si šampion!' : 'Incredible! You are a true champion!';
    }
    if (streak >= 14) {
      return lang === 'hr' ? 'Fantastično! Tvoja predanost je inspirativna!' : 'Fantastic! Your dedication is inspiring!';
    }
    if (streak >= 7) {
      return lang === 'hr' ? 'Odlično! Cijeli tjedan! Nastavi tako!' : 'Great! A whole week! Keep it up!';
    }
    if (streak >= 3) {
      return lang === 'hr' ? 'Super! Tvoj niz raste!' : 'Super! Your streak is growing!';
    }
    return lang === 'hr' ? 'Dobar početak! Nastavi dalje!' : 'Good start! Keep going!';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-heading font-bold text-[#292524]">
                {lang === 'hr' ? 'Tvoj Napredak' : 'Your Progress'}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('streak')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'streak'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Flame className="w-4 h-4 inline mr-1" />
              {lang === 'hr' ? 'Niz' : 'Streak'}
            </button>
            <button
              onClick={() => setActiveTab('quran')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'quran'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-1" />
              {lang === 'hr' ? "Kur'an" : 'Quran'}
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : activeTab === 'streak' ? (
            /* Streak Tab */
            <div className="space-y-6">
              {/* Current Streak */}
              <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
                <div className="text-5xl mb-2">{getStreakEmoji(streakData?.currentStreak || 0)}</div>
                <div className="text-6xl font-bold text-amber-600 mb-1">
                  {streakData?.currentStreak || 0}
                </div>
                <div className="text-amber-700 font-medium">
                  {lang === 'hr' ? 'Dana u nizu' : 'Day Streak'}
                </div>
                <p className="text-sm text-amber-600 mt-3">
                  {getStreakMessage(streakData?.currentStreak || 0, streakData?.hasLoggedToday)}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {streakData?.longestStreak || 0}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Najduži niz' : 'Longest Streak'}
                  </div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <Calendar className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {streakData?.totalActiveDays || 0}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Aktivnih dana' : 'Active Days'}
                  </div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <Star className={`w-6 h-6 mx-auto mb-2 ${streakData?.hasLoggedToday ? 'text-green-500' : 'text-stone-400'}`} />
                  <div className="text-2xl font-bold text-[#292524]">
                    {streakData?.hasLoggedToday ? '✓' : '○'}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Danas' : 'Today'}
                  </div>
                </div>
              </div>

              {/* Streak Badges */}
              <div className="p-4 bg-stone-50 rounded-xl">
                <h3 className="font-semibold text-[#292524] mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  {lang === 'hr' ? 'Dostignuća' : 'Achievements'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[3, 7, 14, 30].map(days => (
                    <div
                      key={days}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        (streakData?.longestStreak || 0) >= days
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-stone-200 text-stone-400'
                      }`}
                    >
                      {days === 3 && (lang === 'hr' ? '3 dana 🌟' : '3 days 🌟')}
                      {days === 7 && (lang === 'hr' ? '7 dana 🔥' : '7 days 🔥')}
                      {days === 14 && (lang === 'hr' ? '14 dana ⭐' : '14 days ⭐')}
                      {days === 30 && (lang === 'hr' ? '30 dana 🏆' : '30 days 🏆')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Quran Stats Tab */
            <div className="space-y-6">
              {/* Progress Circle */}
              <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="12"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="12"
                      strokeDasharray={`${(quranStats?.progressPercent || 0) * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-3xl font-bold text-emerald-600">
                      {quranStats?.progressPercent || 0}%
                    </span>
                  </div>
                </div>
                <div className="text-emerald-700 font-medium">
                  {lang === 'hr' ? "Kur'an Napredak" : 'Quran Progress'}
                </div>
                <p className="text-sm text-emerald-600 mt-1">
                  {quranStats?.totalPagesRead || 0} / 604 {lang === 'hr' ? 'stranica' : 'pages'}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {quranStats?.totalAyahsRead || 0}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Pročitanih ajeta' : 'Ayahs Read'}
                  </div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl">
                  <Headphones className="w-6 h-6 text-blue-500 mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {quranStats?.totalListeningMinutes || 0}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Minuta slušanja' : 'Minutes Listened'}
                  </div>
                  <div className="text-[11px] text-[#78716C] mt-1">
                    {quranStats?.totalListeningSessions || 0} {lang === 'hr' ? 'sesija' : 'sessions'}
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <BookOpen className="w-6 h-6 text-amber-500 mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {quranStats?.completedJuz || 0}/30
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Džuzeva' : 'Juz Completed'}
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-500 mb-2" />
                  <div className="text-2xl font-bold text-[#292524]">
                    {quranStats?.weeklyReadSessions || 0}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {lang === 'hr' ? 'Sesije čitanja (7d)' : 'Read Sessions (7d)'}
                  </div>
                </div>
              </div>

              {/* Weekly Summary */}
              <div className="p-4 bg-stone-50 rounded-xl">
                <h3 className="font-semibold text-[#292524] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  {lang === 'hr' ? 'Ovaj Tjedan' : 'This Week'}
                </h3>
                <div className="flex justify-between text-sm">
                  <div>
                    <span className="text-[#57534E]">{lang === 'hr' ? 'Stranice: ' : 'Pages: '}</span>
                    <span className="font-semibold text-[#292524]">{quranStats?.weeklyPages || 0}</span>
                  </div>
                  <div>
                    <span className="text-[#57534E]">{lang === 'hr' ? 'Slušanje: ' : 'Listening: '}</span>
                    <span className="font-semibold text-[#292524]">
                      {quranStats?.weeklyMinutes || 0} min / {quranStats?.weeklyListeningSessions || 0} {lang === 'hr' ? 'sesija' : 'sessions'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
